/**
 * Actual Delivery Date — when goods physically arrived.
 * Never default to today; reject blank / invalid / future when required.
 */

export const ACTUAL_DELIVERY_DATE_REQUIRED_MESSAGE =
  'Please select the date the goods were actually delivered.'

export const ACTUAL_DELIVERY_DATE_INVALID_MESSAGE =
  'Actual Delivery Date must be a valid calendar date.'

export const ACTUAL_DELIVERY_DATE_FUTURE_MESSAGE =
  'Actual Delivery Date cannot be in the future.'

export const ACTUAL_DELIVERY_DATE_NOT_RECORDED = 'Not Recorded'

export function isBlankActualDeliveryDate(value) {
  return value === undefined || value === null || value === ''
}

function isValidYmd(year, month, day) {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return false
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return false
  const dt = new Date(Date.UTC(year, month - 1, day))
  return (
    dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
  )
}

/**
 * @returns {{ ok: true, ymd: string } | { ok: false, code: string }}
 */
export function parseActualDeliveryDate(value) {
  if (isBlankActualDeliveryDate(value)) {
    return { ok: false, code: 'missing' }
  }

  // Ant Design / moment-like objects
  if (
    value &&
    typeof value === 'object' &&
    typeof value.format === 'function'
  ) {
    const ymd = value.format('YYYY-MM-DD')
    return parseActualDeliveryDate(ymd)
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return { ok: false, code: 'invalid' }
    }
    const ymd = [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, '0'),
      String(value.getDate()).padStart(2, '0'),
    ].join('-')
    return parseActualDeliveryDate(ymd)
  }

  const str = String(value).trim()
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(str)
  if (!match) {
    return { ok: false, code: 'invalid' }
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (!isValidYmd(year, month, day)) {
    return { ok: false, code: 'invalid' }
  }
  return { ok: true, ymd: `${match[1]}-${match[2]}-${match[3]}` }
}

export function isFutureActualDeliveryYmd(ymd, now = new Date()) {
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')
  return String(ymd).slice(0, 10) > today
}

/**
 * @param {unknown} value
 * @param {{ now?: Date, required?: boolean }} [options]
 */
export function validateActualDeliveryDate(
  value,
  { now = new Date(), required = true } = {},
) {
  if (isBlankActualDeliveryDate(value)) {
    if (!required) {
      return { ok: true, ymd: null }
    }
    return {
      ok: false,
      code: 'missing',
      message: ACTUAL_DELIVERY_DATE_REQUIRED_MESSAGE,
    }
  }

  const parsed = parseActualDeliveryDate(value)
  if (!parsed.ok) {
    return {
      ok: false,
      code: parsed.code,
      message:
        parsed.code === 'missing'
          ? ACTUAL_DELIVERY_DATE_REQUIRED_MESSAGE
          : ACTUAL_DELIVERY_DATE_INVALID_MESSAGE,
    }
  }

  if (isFutureActualDeliveryYmd(parsed.ymd, now)) {
    return {
      ok: false,
      code: 'future',
      message: ACTUAL_DELIVERY_DATE_FUTURE_MESSAGE,
    }
  }

  return { ok: true, ymd: parsed.ymd }
}

/** Display YYYY-MM-DD or Not Recorded — never invent a date. */
export function formatActualDeliveryDateForDisplay(value) {
  const parsed = parseActualDeliveryDate(value)
  if (!parsed.ok) return ACTUAL_DELIVERY_DATE_NOT_RECORDED
  return parsed.ymd
}

/**
 * Stamp actualDeliveryDate onto every receipting line in a save payload.
 * No-op when ymd is blank (legacy / omit).
 */
export function applyActualDeliveryDateToLines(lines = [], ymd) {
  if (isBlankActualDeliveryDate(ymd)) return lines
  const check = validateActualDeliveryDate(ymd, { required: true })
  if (!check.ok) return lines
  return (lines || []).map(item => ({
    ...item,
    actualDeliveryDate: check.ymd,
  }))
}
